import type {ReactElement} from 'react';

import {Pagination} from '@/components';
import type {PaginationProps} from '@/components/common/Pagination';
import {TableSkeleton} from '@/components/Table/TableSkeleton';

import BusinessBalanceFilters from './Balances/BusinessBalanceFilters';
import BusinessBalancesTable from './Balances/BusinessBalancesTable';
import useBusinessBalances from './Balances/useBusinessBalances';
import type {BalanceFilter, BusinessWalletBalanceProps} from './types';

const emptyMessages: Record<BalanceFilter, string> = {
  funded: 'No currency has a balance yet.',
  owing: 'No fees owed in any currency.',
  all: 'No currencies found.',
};

const BusinessWalletBalance = ({
  businessId,
  businessName,
  walletBalances,
  isLoadingWalletBalances,
  onRequestFund,
}: BusinessWalletBalanceProps): ReactElement => {
  const {
    list,
    total,
    counts,
    searchTerm,
    handleSearch,
    filter,
    handleFilter,
    sort,
    handleSort,
    setPage,
    isPageReset,
    isSearch,
  } = useBusinessBalances(walletBalances);

  const loadedContent = list.length ? (
    <BusinessBalancesTable
      businessId={businessId}
      list={list}
      onRequestFund={onRequestFund}
    />
  ) : (
    <div className="flex items-center gap-1 justify-center mt-5">
      <p
        data-cy="business-details-balances-empty"
        className="text-gray-800 dark:text-gray-300"
      >
        {isSearch ? 'No currencies match your search.' : emptyMessages[filter]}
      </p>
    </div>
  );

  return (
    <>
      <h3
        data-cy="business-details-balances-title"
        className="font-semibold text-gray-900 dark:text-gray-300"
      >
        Balances
      </h3>
      {businessName && (
        <p
          data-cy="business-details-balances-business-name"
          className="mb-5 text-sm text-gray-500 dark:text-gray-400"
        >
          {businessName}
        </p>
      )}

      <BusinessBalanceFilters
        searchTerm={searchTerm}
        onSearchChange={handleSearch}
        filter={filter}
        onFilterChange={handleFilter}
        counts={counts}
        sort={sort}
        onSortChange={handleSort}
      />

      <main className="pt-5 py-10 overflow-x-auto">
        {isLoadingWalletBalances ? (
          <div className="min-w-full">
            <TableSkeleton />
          </div>
        ) : (
          loadedContent
        )}

        {!isLoadingWalletBalances && list.length ? (
          <Pagination
            isPageReset={isPageReset}
            onPageChange={
              ((params) =>
                setPage(
                  params.page as number,
                )) as PaginationProps['onPageChange']
            }
            total={total}
            isSearch={isSearch}
          />
        ) : null}
      </main>
    </>
  );
};

export default BusinessWalletBalance;
